import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { BottomUXSpace } from '../../components';
import { GetExecutiveRange, RefreshExecutiveToday } from '../../services/backend';
import {
  PRESETS,
  defaultMtdRange,
  lastSixMonthsRange,
  rangeForPreset,
  formatPkrCompact,
  formatPkrFull,
  formatPct,
  formatSynced,
  drillLinks,
  capAttention,
  severityTag,
  findBuilding,
  buildGrowthFunnel,
  buildWaterfall,
  rentPerOccupiedSeat,
  buildFillScenario,
  buildCommandHeroes,
  formatDelta,
  CC_SECTION_TITLES,
  CC_SUBHEADINGS,
  filterCoaDrillAccounts,
  isCoaUnderPosted,
  formatCoaAccountLabel,
  formatSignedPkr,
  varianceTone,
  formatBvaMonthLabel,
} from './utils';
import ExecutiveErrorBoundary, { ChartErrorBoundary } from './ErrorBoundary';
import './executive.css';

const Section = ({ id, title, className = '', children, loading, error }) => (
  <section id={id} className={`dash-section ${className}`} aria-label={title}>
    <div className="section-label">{title}</div>
    {loading ? <div className="loading-state card">Loading…</div> : null}
    {!loading && error ? <div className="error-state card">{error}</div> : null}
    {!loading && !error ? children : null}
  </section>
);

/**
 * Lazy-safe Recharts host: only mounts ResponsiveContainer when window exists
 * and the container has positive width/height. Chart errors stay local.
 */
const SafeChart = ({ children, height = 220, emptyLabel = 'Chart unavailable' }) => {
  const ref = useRef(null);
  const [ready, setReady] = useState(false);

  useLayoutEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const el = ref.current;
    if (!el) return undefined;

    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight || height;
      setReady(w > 0 && h > 0);
    };

    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro) ro.observe(el);
    window.addEventListener('resize', measure);
    const t = window.setTimeout(measure, 50);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener('resize', measure);
      window.clearTimeout(t);
    };
  }, [height]);

  if (typeof window === 'undefined') {
    return <div className="empty-state">{emptyLabel}</div>;
  }

  return (
    <div ref={ref} className="chart-wrap" style={{ width: '100%', height }}>
      {ready ? (
        <ChartErrorBoundary fallback={<div className="empty-state">{emptyLabel}</div>}>
          <ResponsiveContainer width="100%" height="100%">
            {children}
          </ResponsiveContainer>
        </ChartErrorBoundary>
      ) : (
        <div className="empty-state">Preparing chart…</div>
      )}
    </div>
  );
};

const Spark = ({ points, color }) => {
  if (!points) {
    return (
      <div className="spark empty" title="Sparkline unavailable" aria-label="Sparkline unavailable">
        <svg viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden>
          <line x1="0" y1="14" x2="100" y2="14" stroke="#cbd5e1" strokeWidth="1.5" strokeDasharray="4 3" />
        </svg>
      </div>
    );
  }
  return (
    <svg className="spark" viewBox="0 0 100 28" preserveAspectRatio="none" aria-hidden>
      <polyline fill="none" stroke={color || '#0D9488'} strokeWidth="2" points={points} />
    </svg>
  );
};

const Executive = () => {
  const initial = useMemo(() => defaultMtdRange(), []);
  const [draftPreset, setDraftPreset] = useState(initial.preset);
  const [draftFrom, setDraftFrom] = useState(initial.from);
  const [draftTo, setDraftTo] = useState(initial.to);
  const [applied, setApplied] = useState(initial);
  const [data, setData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [coaDrillCategory, setCoaDrillCategory] = useState(null); // null | 'all' | category name

  const loadRange = useCallback(async (from, to, { refreshToday = false } = {}) => {
    setLoading(true);
    setError(null);
    try {
      const six = lastSixMonthsRange();
      const [res, trendRes] = await Promise.all([
        refreshToday
          ? RefreshExecutiveToday({ from, to })
          : GetExecutiveRange({ from, to }),
        GetExecutiveRange({ from: six.from, to: six.to }).catch(() => null),
      ]);
      const payload = res?.data?.data;
      if (!payload) {
        setData(null);
        setError('No data returned from executive API');
      } else {
        setData(payload);
        if (payload.empty) {
          setError(payload.message || 'No snapshots in range');
        }
      }
      const trendPayload = trendRes?.data?.data;
      setTrendData(trendPayload && !trendPayload.empty ? trendPayload : payload);
    } catch (err) {
      console.error('Executive load failed', err);
      setData(null);
      setError(err?.response?.data?.message || err.message || 'Failed to load executive dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadRange(applied.from, applied.to);
  }, [applied.from, applied.to, loadRange]);

  const onPreset = (preset) => {
    const next = rangeForPreset(preset, draftFrom, draftTo);
    setDraftPreset(next.preset);
    setDraftFrom(next.from);
    setDraftTo(next.to);
  };

  const onApply = () => {
    const preset = draftPreset === 'custom' ? 'custom' : draftPreset;
    setApplied({ preset, from: draftFrom, to: draftTo });
  };

  const onRefreshToday = async () => {
    setRefreshing(true);
    await loadRange(applied.from, applied.to, { refreshToday: true });
  };

  const snap = data?.snapshot;
  const money = data?.money;
  const ops = data?.operations;
  const pipeline = data?.pipeline;
  const marketing = data?.marketing;
  const bvaMonthly = data?.bvaMonthly;
  const ask = data?.todaysAsk;
  const sources = data?.sources || {};
  const gapCount = data?.missingDates?.length || 0;

  const trendSeries = useMemo(
    () => trendData?.money?.trends?.series || money?.trends?.series || [],
    [trendData, money]
  );

  const heroes = useMemo(
    () => buildCommandHeroes(data, trendSeries),
    [data, trendSeries]
  );

  const attention = useMemo(
    () => capAttention(data?.needAttention || [], 5),
    [data?.needAttention]
  );

  const launchpad = useMemo(
    () => findBuilding(snap?.buildings, 'Launchpad'),
    [snap?.buildings]
  );
  const nexus = useMemo(
    () => findBuilding(snap?.buildings, 'Nexus'),
    [snap?.buildings]
  );

  const waterfall = useMemo(
    () => buildWaterfall({
      invoiced: snap?.rent?.dashboardRentPkr,
      collected: money?.collections?.receivedPkr,
      arUnpaid: money?.ar?.unpaid,
    }),
    [snap?.rent?.dashboardRentPkr, money?.collections?.receivedPkr, money?.ar?.unpaid]
  );

  const growth = useMemo(
    () => buildGrowthFunnel(pipeline?.leads, snap?.seats?.occupied),
    [pipeline?.leads, snap?.seats?.occupied]
  );

  const avgRentSeat = useMemo(
    () => rentPerOccupiedSeat(snap?.rent?.dashboardRentPkr, snap?.seats?.occupied),
    [snap?.rent?.dashboardRentPkr, snap?.seats?.occupied]
  );

  const fillScenario = useMemo(
    () => buildFillScenario({ vacant: snap?.seats?.vacant, rentPerSeat: avgRentSeat, fillN: 10 }),
    [snap?.seats?.vacant, avgRentSeat]
  );

  const occupancyMix = useMemo(() => {
    const seats = snap?.seats || {};
    return [
      { name: 'Occupied', value: seats.occupied || 0, color: '#0d9488' },
      { name: 'Frozen', value: seats.frozen || 0, color: '#64748b' },
      { name: 'Vacant', value: seats.vacant || 0, color: '#f59e0b' },
      { name: 'UC', value: seats.underConstruction || 0, color: '#cbd5e1' },
    ];
  }, [snap?.seats]);

  const coaDrillAccounts = useMemo(
    () => filterCoaDrillAccounts(bvaMonthly?.accounts, coaDrillCategory),
    [bvaMonthly?.accounts, coaDrillCategory]
  );

  const toggleCoaDrill = (category) => {
    setCoaDrillCategory((prev) => (prev === category ? null : category));
  };

  return (
    <div className="exec-dash cc-dash p-3 md:p-4">
      <header className="exec-header">
        <div className="exec-header-top">
          <div>
            <h1>Workbench Executive · Command Center</h1>
            <div className="exec-sub">
              Valencia · Launchpad + Nexus · Staff-only · Asia/Karachi
            </div>
          </div>
          <div className="exec-meta">
            <span>{new Date().toLocaleDateString('en-PK', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' })}</span>
            <span>Last synced: {formatSynced(data?.lastSynced)}</span>
            <span className={`exec-chip pulse ${sources.pulse ? '' : 'off'}`}>
              <span className="exec-dot" /> Pulse
            </span>
            <span className={`exec-chip zoho ${sources.zoho ? '' : 'off'}`}>
              Zoho Work Bench
            </span>
            <span className={`exec-chip gbp ${sources.gbp ? '' : 'off'}`}>GBP</span>
          </div>
        </div>

        <div className="date-range" role="group" aria-label="Snapshot date range">
          <div className="date-range-row">
            <div className="date-presets">
              {Object.entries(PRESETS).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  className={`preset-chip ${draftPreset === key ? 'active' : ''}`}
                  onClick={() => onPreset(key)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="date-inputs">
              <label>
                From
                <input
                  type="date"
                  value={draftFrom}
                  onChange={(e) => {
                    setDraftFrom(e.target.value);
                    setDraftPreset('custom');
                  }}
                />
              </label>
              <label>
                To
                <input
                  type="date"
                  value={draftTo}
                  onChange={(e) => {
                    setDraftTo(e.target.value);
                    setDraftPreset('custom');
                  }}
                />
              </label>
              <button type="button" className="btn-apply" onClick={onApply} disabled={loading}>
                Apply
              </button>
              <button type="button" className="btn-refresh" onClick={onRefreshToday} disabled={loading || refreshing}>
                {refreshing ? 'Refreshing…' : 'Refresh now'}
              </button>
            </div>
          </div>
          <div className="snapshot-meta">
            <span className="snapshot-label">
              Snapshot range: {applied.from} → {applied.to} ({PRESETS[applied.preset] || 'Custom'})
            </span>
            <span className="snapshot-note">
              Reads snapshot DB · live capture via daily job / Refresh now (today only)
            </span>
            {gapCount > 0 ? (
              <span className="gap-note">{gapCount} missing snapshot day(s) in range</span>
            ) : null}
          </div>
        </div>
      </header>

      {error ? (
        <div className="exec-alert-banner" role="alert">
          <div className="exec-alert-title">Executive data issue</div>
          <div className="exec-alert-msg">{error}</div>
          <button type="button" className="btn-refresh" onClick={onRefreshToday} disabled={loading || refreshing}>
            {refreshing ? 'Refreshing…' : 'Refresh now'}
          </button>
        </div>
      ) : null}

      {/* 1. Command bar */}
      <Section id="command-bar" title={CC_SECTION_TITLES.commandBar} loading={loading && !data}>
        <div className="cc-heroes">
          {heroes.map((h) => {
            const delta = formatDelta(h.delta, h.deltaSuffix || '');
            return (
              <div className="cc-hero card" key={h.key}>
                <div className="cc-hero-label">{h.label}</div>
                <div className={`cc-hero-value ${h.danger ? 'danger' : ''}`}>
                  {h.value == null ? '—' : h.value}
                </div>
                <div className={`cc-hero-delta ${delta ? delta.tone : 'flat'}`}>
                  {delta ? delta.text : h.hint}
                  {h.degraded ? ' · unavailable' : ''}
                </div>
                <Spark points={h.spark} color={h.sparkColor} />
              </div>
            );
          })}
        </div>
      </Section>

      {/* 2. Need Attention */}
      <Section id="need-attention" title={CC_SECTION_TITLES.needAttention} className="need-attention" loading={loading && !data}>
        {attention.length === 0 ? (
          <div className="empty-state card">All clear — no attention flags for this range.</div>
        ) : (
          <div className="cc-attn-grid">
            {attention.map((flag) => {
              const tag = severityTag(flag.severity);
              return (
                <div key={flag.label} className={`card cc-attn ${flag.severity === 'critical' ? '' : 'warn'}`}>
                  <span className={`cc-tag ${tag.warn ? 'warn' : ''}`}>{tag.text}</span>
                  <h3>{flag.label}</h3>
                  <p>
                    {flag.value}
                    {flag.detail ? ` · ${flag.detail}` : ''}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </Section>

      {/* 3. Capacity & Sites */}
      <Section id="capacity" title={CC_SECTION_TITLES.capacity} loading={loading && !data}>
        <div className="cc-grid-2">
          <div className="card">
            <h2 className="cc-block">Valencia occupancy</h2>
            <div className="cc-flex">
              <div className="cc-donut-wrap">
                <div className="cc-donut-center">
                  {formatPct(snap?.seats?.occupancyPct)}
                  <small>Occupancy</small>
                </div>
                <div className="cc-donut-ring" aria-hidden>
                  {occupancyMix.map((seg) => (
                    <div
                      key={seg.name}
                      className="cc-donut-seg"
                      style={{
                        flexGrow: seg.value,
                        background: seg.color,
                        display: seg.value ? 'block' : 'none',
                      }}
                      title={`${seg.name} ${seg.value}`}
                    />
                  ))}
                </div>
              </div>
              <div className="cc-legend">
                <div><span style={{ background: '#0D9488' }} />Occupied {snap?.seats?.occupied ?? '—'}</div>
                <div><span style={{ background: '#64748B' }} />Frozen {snap?.seats?.frozen ?? '—'}</div>
                <div><span style={{ background: '#F59E0B' }} />Vacant {snap?.seats?.vacant ?? '—'}</div>
                <div><span style={{ background: '#CBD5E1' }} />Under construction {snap?.seats?.underConstruction ?? '—'}</div>
                <div className="cc-legend-total">Total seats {snap?.seats?.total ?? '—'} · avail {snap?.seats?.available ?? '—'}</div>
              </div>
            </div>
          </div>
          <div className="card">
            <h2 className="cc-block">{CC_SUBHEADINGS.launchpadVsNexus}</h2>
            {[
              { b: launchpad, label: 'Launchpad', color: '#0D9488' },
              { b: nexus, label: 'Nexus', color: '#14324F' },
            ].map(({ b, label, color }) => (
              <div className="cc-bar-row" key={label}>
                <div className="cc-bar-top">
                  <span>{label}</span>
                  <span>
                    {b
                      ? `${formatPct(b.occupancyPct)} · ${b.occupied ?? '—'}/${b.total ?? '—'}`
                      : '— / unavailable'}
                  </span>
                </div>
                <div className="cc-track">
                  <div
                    className="cc-seg"
                    style={{
                      width: b?.occupancyPct != null ? `${Math.min(100, b.occupancyPct)}%` : '0%',
                      background: color,
                      opacity: b ? 1 : 0.35,
                    }}
                  />
                </div>
                <div className="cc-chips-mini">
                  <span className="cc-mini">Occ {b?.occupied ?? '—'}</span>
                  <span className="cc-mini">Frozen {b?.frozen ?? '—'}</span>
                  <span className="cc-mini">Vacant {b?.vacant ?? '—'}</span>
                  <span className="cc-mini">UC {b?.underConstruction ?? '—'}</span>
                </div>
              </div>
            ))}
            {!launchpad && !nexus ? (
              <p className="cc-muted">
                Per-building grain not in snapshot yet — showing Launchpad / Nexus as unavailable.
                Refresh now after WBP-178 capture, or await daily job.
              </p>
            ) : null}
            <p className="cc-muted">
              No third building ·{' '}
              <Link className="drill" to={drillLinks.seats} target="_blank" rel="noreferrer">
                drill → Seats
              </Link>
            </p>
          </div>
        </div>
      </Section>

      {/* 4. Revenue waterfall + Growth funnel */}
      <Section id="revenue" title={CC_SECTION_TITLES.revenue} loading={loading && !data}>
        <div className="cc-grid-2">
          <div className="card">
            <h2 className="cc-block">{CC_SUBHEADINGS.revenueWaterfall} · Invoiced → Collected → Outstanding</h2>
            <div className="cc-waterfall">
              {waterfall.map((col) => (
                <div className="cc-wf" key={col.key}>
                  <div
                    className="cc-wf-col"
                    style={{
                      height: col.heightPct == null ? 8 : `${Math.round((col.heightPct / 100) * 120)}px`,
                      background: col.heightPct == null ? '#E2E8F0' : col.color,
                      opacity: col.heightPct == null ? 0.6 : 1,
                    }}
                  />
                  <div className="cc-wf-n">
                    {col.amount == null ? '—' : formatPkrFull(col.amount)}
                  </div>
                  <div className="cc-wf-l">{col.label}</div>
                  <div className="cc-wf-src">{col.source}</div>
                </div>
              ))}
            </div>
            <div className="cc-aging">
              <div className="cc-aging-box">
                <span>AR current</span>
                <b>{money?.ar ? `PKR ${formatPkrFull(money.ar.current)}` : '—'}</b>
              </div>
              <div className="cc-aging-box">
                <span>AR overdue</span>
                <b>{money?.ar ? `PKR ${formatPkrFull(money.ar.overdue)}` : '—'}</b>
              </div>
              <div className="cc-aging-box">
                <span>Security held</span>
                <b>
                  {snap?.rent?.securityThisMonth != null
                    ? `PKR ${formatPkrFull(snap.rent.securityThisMonth)}`
                    : '—'}
                </b>
              </div>
              <div className="cc-aging-box">
                <span>Advance</span>
                <b>
                  {snap?.rent?.advanceThisMonth != null
                    ? `PKR ${formatPkrFull(snap.rent.advanceThisMonth)}`
                    : '—'}
                </b>
              </div>
            </div>
            {!money?.available ? (
              <div className="warn-banner" style={{ marginTop: 10 }}>
                Zoho finance unavailable — {sources.zohoError || 'configure ZOHO_*'}. Collected / AR stay empty (no invented numbers).
              </div>
            ) : money?.collections?.receivedPkr == null ? (
              <div className="warn-banner" style={{ marginTop: 10 }}>
                Collections missing on snapshot — {money?.collections?.note || 'Refresh now after WBP-178 job'}.
              </div>
            ) : (
              <p className="cc-muted" style={{ marginTop: 10 }}>
                Invoiced from Pulse · Collected from Zoho payments · AR from Zoho Work Bench
              </p>
            )}
          </div>
          <div className="card">
            <h2 className="cc-block">
              Growth funnel
              <span className="cc-block-sub">New in range: {pipeline?.leads?.newInRange ?? '—'}</span>
            </h2>
            <div className="cc-funnel">
              {growth.map((step) => (
                <div
                  className="cc-funnel-step"
                  key={step.key}
                  style={{ width: `${step.widthPct || 100}%` }}
                >
                  <span>{step.label}</span>
                  <span>{step.count == null ? '—' : step.count}</span>
                </div>
              ))}
            </div>
            <p className="cc-muted" style={{ marginTop: 10 }}>
              Pipeline from Pulse Leads · occupied seats from Valencia snapshot
            </p>
          </div>
        </div>
      </Section>

      {/* 5. Economics · churn · trends */}
      <Section id="economics" title={CC_SECTION_TITLES.economics} loading={loading && !data}>
        <div className="cc-grid-3">
          <div className="card">
            <h2 className="cc-block">{CC_SUBHEADINGS.perSeat}</h2>
            <div className="cc-legend" style={{ fontSize: 13 }}>
              <div>
                <b>Invoice rent / occupied seat</b>
                <br />
                {avgRentSeat == null ? '—' : `PKR ${formatPkrFull(avgRentSeat)}`}
              </div>
              <div style={{ marginTop: 8 }}>
                <b>Rev / available seat (Zoho P&amp;L)</b>
                <br />
                {money?.perSeat?.revenuePerSeat == null
                  ? '—'
                  : `PKR ${formatPkrFull(money.perSeat.revenuePerSeat)}`}
              </div>
              <div style={{ marginTop: 8 }}>
                <b>Opex envelope target</b>
                <br />
                ~70% of revenue · Monthly BvA below
              </div>
            </div>
            {fillScenario ? (
              <div className="cc-scenario">
                Scenario: fill {fillScenario.fillN} vacant @ PKR {formatPkrFull(fillScenario.rentPerSeat)}
                {' '}→ +PKR {formatPkrFull(fillScenario.monthlyUplift)} / mo
              </div>
            ) : (
              <div className="cc-scenario muted">
                Fill-vacant scenario unavailable — need occupied seats + invoice rent.
              </div>
            )}
          </div>
          <div className="card">
            <h2 className="cc-block">{CC_SUBHEADINGS.churn}</h2>
            <div className="cc-chips-mini" style={{ margin: '0 0 10px' }}>
              <span className="cc-mini">Frozen seats {snap?.seats?.frozen ?? '—'}</span>
              <span className="cc-mini">Frozen customers {snap?.customers?.frozen ?? '—'}</span>
              <span className="cc-mini">Inactive customers {snap?.customers?.inactive ?? '—'}</span>
            </div>
            <p className="cc-muted">
              Expired freeze / notice-period not in snapshot yet — watch frozen seats before they go dark ·{' '}
              <Link className="drill" to={drillLinks.customersFrozen} target="_blank" rel="noreferrer">
                drill → Customers
              </Link>
            </p>
          </div>
          <div className="card">
            <h2 className="cc-block">
              {CC_SUBHEADINGS.sixMoTrends}
              <span className="cc-block-sub">
                {trendData?.money?.trends?.grain || money?.trends?.grain || 'daily'} · long-range chart
              </span>
            </h2>
            {trendSeries.length ? (
              <SafeChart height={160} emptyLabel="Trends chart unavailable">
                <LineChart data={trendSeries}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} hide={trendSeries.length > 40} />
                  <YAxis yAxisId="left" tick={{ fontSize: 10 }} domain={[0, 100]} width={32} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 10 }} width={40} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Line yAxisId="left" type="monotone" dataKey="occupancyPct" name="Occupancy %" stroke="#0d9488" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="dashboardRent" name="Invoice rent" stroke="#0B1F33" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="collectionsReceived" name="Collections" stroke="#059669" strokeWidth={2} dot={false} connectNulls={false} />
                </LineChart>
              </SafeChart>
            ) : (
              <div className="empty-state">
                No trend points yet — need daily snapshots. Use Refresh now to seed today.
              </div>
            )}
          </div>
        </div>
      </Section>

      {/* 6. Ops heat teasers */}
      <Section id="ops-heat" title={CC_SECTION_TITLES.opsHeat} loading={loading && !data}>
        <div className="cc-grid-4">
          <div className="card">
            <div className="cc-hero-label">Support</div>
            <div className="cc-teaser-value">
              Open {ops?.support?.open ?? '—'}
              {ops?.support?.slaPct != null ? ` · SLA ${ops.support.slaPct}%` : ''}
            </div>
            <Link className="drill" to={drillLinks.supportOpen} target="_blank" rel="noreferrer">
              drill → Support board
            </Link>
          </div>
          <div className="card">
            <div className="cc-hero-label">Maintenance</div>
            <div className="cc-teaser-value">
              {ops?.maintenance?.overdue != null ? `${ops.maintenance.overdue} overdue` : '—'}
            </div>
            <Link className="drill" to={drillLinks.maintenanceOverdue} target="_blank" rel="noreferrer">
              drill → Maintenance
            </Link>
          </div>
          <div className="card">
            <div className="cc-hero-label">Inventory</div>
            <div className="cc-teaser-value">
              {ops?.inventory?.lowStockCount != null ? `${ops.inventory.lowStockCount} low stock` : '—'}
            </div>
            <Link className="drill" to={drillLinks.inventoryLow} target="_blank" rel="noreferrer">
              drill → Inventory
            </Link>
          </div>
          <div className="card">
            <div className="cc-hero-label">Meetings</div>
            <div className="cc-teaser-value">
              Today {ops?.meetingRooms?.bookingsLatestDay ?? '—'}
              <span className="cc-muted"> · range {ops?.meetingRooms?.bookingsInRange ?? '—'}</span>
            </div>
            <Link className="drill" to={drillLinks.meetings} target="_blank" rel="noreferrer">
              drill → Meeting rooms
            </Link>
          </div>
        </div>
      </Section>

      {/* 7. GBP · BvA teaser · Today's Ask */}
      <Section id="marketing-bva-ask" title={CC_SECTION_TITLES.marketing} loading={loading && !data}>
        <div className="cc-grid-3">
          <div className="card">
            <h2 className="cc-block">Google Business Profile</h2>
            {marketing?.gbp?.available ? (
              <p style={{ margin: 0, fontSize: 13 }}>
                <b>★ {marketing.gbp.reviewsScore ?? '—'}</b>
                {' '}· {marketing.gbp.reviewsCount ?? 0} reviews
                <br />
                <span className="cc-muted">
                  Views {marketing.gbp.views ?? '—'} · Searches {marketing.gbp.searches ?? '—'} · Interactions {marketing.gbp.interactions ?? '—'}
                </span>
                <br />
                <span className="cc-muted">GBP only — not Ads</span>
              </p>
            ) : (
              <div className="empty-state">
                GBP unavailable — {marketing?.gbp?.error || sources.gbpError || 'configure GBP_* env vars'}.
                Section degraded; no invented metrics.
              </div>
            )}
            {marketing?.meta?.ctwaBlocked ? (
              <div className="warn-banner" style={{ marginTop: 10 }}>
                Meta CTWA blocked — {marketing.meta.note || 'status note only'}
              </div>
            ) : null}
          </div>

          <div className="card cc-bva-card" style={{ gridColumn: '1 / -1' }}>
            <div className="cc-bva-head">
              <div>
                <h2 className="cc-block" style={{ margin: 0 }}>
                  Monthly Budget vs Actual
                  {bvaMonthly?.month
                    ? ` · ${formatBvaMonthLabel(bvaMonthly.month)}${bvaMonthly?.periodTo ? ' MTD' : ''}`
                    : ''}
                </h2>
                <p className="cc-bva-sub">
                  Zoho Work Bench actuals vs Workbench opex envelope (~70%).
                  Click a category to drill CoA accounts with budget ≠ 0 or expense ≠ 0 this month.
                </p>
              </div>
              {bvaMonthly?.available === false || !bvaMonthly?.available ? (
                <span className="cc-bva-warn">Degraded / unavailable — budgets not invented</span>
              ) : (bvaMonthly?.categories || []).some((c) => c.underPosted) ? (
                <span className="cc-bva-warn">Under-posted — do not baseline margin</span>
              ) : null}
            </div>

            {!bvaMonthly?.available ? (
              <>
                <div className="empty-state">
                  {bvaMonthly?.error
                    || 'zoho.bva not available on snapshot — honest empty (never invent budgets).'}
                  {bvaMonthly?.summary?.revenueActual != null ? (
                    <div style={{ marginTop: 8 }}>
                      Revenue actual (from P&amp;L when present): PKR {formatPkrFull(bvaMonthly.summary.revenueActual)}
                    </div>
                  ) : null}
                  {bvaMonthly?.summary?.opexActual != null ? (
                    <div style={{ marginTop: 4 }}>
                      Opex actual (from P&amp;L when present): PKR {formatPkrFull(bvaMonthly.summary.opexActual)}
                    </div>
                  ) : null}
                </div>
                {(bvaMonthly?.accounts || []).length ? (
                  <div className="cc-bva-drill">
                    <div className="cc-bva-drill-head">
                      <strong>Actual CoA lines (budgets unavailable)</strong>
                      <button
                        type="button"
                        className="cc-bva-drill-toggle"
                        onClick={() => toggleCoaDrill(coaDrillCategory === 'all' ? null : 'all')}
                      >
                        {coaDrillCategory === 'all' ? 'Hide' : 'Show'} accounts
                      </button>
                    </div>
                    {coaDrillCategory === 'all' ? (
                      <div className="table-wrap">
                        <table className="exec-table cc-bva-coa-table">
                          <thead>
                            <tr>
                              <th>Account (Zoho CoA)</th>
                              <th>Budget PKR</th>
                              <th>Actual PKR</th>
                              <th>Variance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {coaDrillAccounts.map((acc, i) => {
                              const varPkr = acc.variancePkr != null
                                ? acc.variancePkr
                                : (Number(acc.budgetPkr) || 0) - (Number(acc.expensePkr) || 0);
                              const under = isCoaUnderPosted(acc);
                              return (
                                <tr key={acc.accountId || `${acc.name}-${i}`} className={under ? 'under-posted' : ''}>
                                  <td>
                                    {formatCoaAccountLabel(acc)}
                                    {under ? <span className="cc-flag under">under-posted</span> : null}
                                  </td>
                                  <td>—</td>
                                  <td>{formatPkrFull(acc.expensePkr)}</td>
                                  <td>—</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : null}
                    <p className="cc-muted">Budgets unavailable — showing expense≠0 CoA lines only; amounts not invented.</p>
                  </div>
                ) : null}
              </>
            ) : (
              <>
                <div className="cc-bva-summary">
                  <div className="cc-aging-box">
                    <span>Revenue (actual)</span>
                    <b>PKR {formatPkrFull(bvaMonthly.summary?.revenueActual)}</b>
                  </div>
                  <div className="cc-aging-box">
                    <span>Opex budget (~70% env.)</span>
                    <b>PKR {formatPkrFull(bvaMonthly.summary?.opexBudget)}</b>
                  </div>
                  <div className="cc-aging-box">
                    <span>Opex actual posted</span>
                    <b>PKR {formatPkrFull(bvaMonthly.summary?.opexActual)}</b>
                  </div>
                  <div className="cc-aging-box">
                    <span>Variance (budget − actual)</span>
                    <b className={`cc-var ${varianceTone(bvaMonthly.summary?.variance)}`}>
                      PKR {formatSignedPkr(bvaMonthly.summary?.variance)}
                    </b>
                  </div>
                </div>

                {(bvaMonthly.categories || []).length ? (
                  <div className="table-wrap">
                    <table className="exec-table cc-bva-table">
                      <thead>
                        <tr>
                          <th>Category</th>
                          <th>Budget %</th>
                          <th>Budget PKR</th>
                          <th>Actual PKR</th>
                          <th>Variance</th>
                          <th aria-label="Drill" />
                        </tr>
                      </thead>
                      <tbody>
                        {bvaMonthly.categories.map((row) => {
                          const active = coaDrillCategory === row.name;
                          return (
                            <tr
                              key={row.name}
                              className={`cc-bva-cat-row${active ? ' active' : ''}${row.underPosted ? ' under-posted' : ''}`}
                              onClick={() => toggleCoaDrill(row.name)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  toggleCoaDrill(row.name);
                                }
                              }}
                              tabIndex={0}
                              role="button"
                              aria-expanded={active}
                              aria-label={`Drill ${row.name} CoA accounts`}
                            >
                              <td>
                                {row.name}
                                {row.underPosted ? <span className="cc-flag under">under-posted</span> : null}
                                {row.needsMap ? <span className="cc-flag needs">needs map</span> : null}
                              </td>
                              <td>{row.budgetPct != null ? `${row.budgetPct}%` : '—'}</td>
                              <td>{formatPkrFull(row.budgetPkr)}</td>
                              <td>{formatPkrFull(row.actualPkr)}</td>
                              <td className={`cc-var ${varianceTone(row.variancePkr)}`}>
                                {formatSignedPkr(row.variancePkr)}
                              </td>
                              <td className="cc-bva-drill-link">{active ? 'Hide ↑' : 'Drill →'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="empty-state">No category rows in zoho.bva</div>
                )}

                <div className="cc-bva-drill-actions">
                  <button
                    type="button"
                    className={`cc-bva-drill-toggle${coaDrillCategory === 'all' ? ' active' : ''}`}
                    onClick={() => toggleCoaDrill('all')}
                  >
                    {coaDrillCategory === 'all' ? 'Hide all CoA accounts' : 'Show all CoA accounts'}
                  </button>
                </div>

                {coaDrillCategory ? (
                  <div className="cc-bva-drill">
                    <div className="cc-bva-drill-head">
                      <strong>
                        CoA drill
                        {coaDrillCategory === 'all' ? ' · all accounts' : ` · ${coaDrillCategory}`}
                        {' · '}
                        budget ≠ 0 or expense ≠ 0
                        {bvaMonthly.month ? ` · ${bvaMonthly.month}` : ''}
                      </strong>
                    </div>
                    {coaDrillAccounts.length ? (
                      <div className="table-wrap">
                        <table className="exec-table cc-bva-coa-table">
                          <thead>
                            <tr>
                              <th>Account (Zoho CoA)</th>
                              <th>Budget PKR</th>
                              <th>Actual PKR</th>
                              <th>Variance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {coaDrillAccounts.map((acc, i) => {
                              const varPkr = acc.variancePkr != null
                                ? acc.variancePkr
                                : (Number(acc.budgetPkr) || 0) - (Number(acc.expensePkr) || 0);
                              const under = isCoaUnderPosted(acc);
                              return (
                                <tr key={acc.accountId || `${acc.name}-${i}`} className={under ? 'under-posted' : ''}>
                                  <td>
                                    {formatCoaAccountLabel(acc)}
                                    {under ? <span className="cc-flag under">under-posted</span> : null}
                                  </td>
                                  <td>{formatPkrFull(acc.budgetPkr)}</td>
                                  <td>{formatPkrFull(acc.expensePkr)}</td>
                                  <td className={`cc-var ${varianceTone(varPkr)}`}>
                                    {formatSignedPkr(varPkr)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="empty-state">
                        No CoA accounts with budget ≠ 0 or expense ≠ 0
                        {coaDrillCategory !== 'all' ? ` for ${coaDrillCategory}` : ''}.
                      </div>
                    )}
                    <p className="cc-muted">
                      Rule: every expense CoA line where budget ≠ 0 OR expense ≠ 0 for the snapshot month.
                      Zero/zero rows hidden.
                    </p>
                  </div>
                ) : null}
              </>
            )}
          </div>

          <div className="card" style={{ gridColumn: '1 / -1' }}>
            <h2 className="cc-block">Today&apos;s Ask</h2>
            <div className="cc-ask">
              {(ask?.items || []).length ? (
                ask.items.slice(0, 3).map((item, i) => (
                  <div className="card cc-ask-card" key={i}>
                    <span className="cc-ask-num">{String(i + 1).padStart(2, '0')}</span>
                    <div className="cc-ask-text">{item}</div>
                  </div>
                ))
              ) : (
                <div className="empty-state">No synthesized asks — quiet day or await first snapshot.</div>
              )}
            </div>
            <div className="cc-ask-drills">
              <Link to={drillLinks.supportOpen} target="_blank" rel="noreferrer">Support open</Link>
              <Link to={drillLinks.maintenanceOpen} target="_blank" rel="noreferrer">Maintenance</Link>
              <Link to={drillLinks.customersActive} target="_blank" rel="noreferrer">Customers</Link>
              <Link to={drillLinks.inventoryLow} target="_blank" rel="noreferrer">Inventory low</Link>
            </div>
          </div>
        </div>
      </Section>

      <BottomUXSpace />
    </div>
  );
};

const ExecutivePage = () => (
  <ExecutiveErrorBoundary>
    <Executive />
  </ExecutiveErrorBoundary>
);

export default ExecutivePage;
